import { ConversationStatus } from '@provision/conversation-types';
import { useCallback } from 'react';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import { Conversation, useGroupRules, UserGroupRules } from 'src/graphql';
import { useConversationDelete } from 'src/graphql/entities/conversation/use-conversation-delete';
import { useConversationUpdate } from 'src/graphql/entities/conversation/use-conversation-update';
import { useRestoreConversation } from 'src/graphql/entities/conversation/use-restore-conversation';
import { useConfirmationModal, useNotifications } from 'src/utils/hooks';
import { ConversationStatePanel } from './conversation-state-panel';
import { StatePanelButton } from './conversation-state-panel.styles';
import { ConversationFooterState } from './use-conversation-footer-state';

const STATE_PANEL_ICON_SIZE = 15;

interface ConversationFooterStatePanelProps {
  state: ConversationFooterState;
  conversation: Conversation;
  onPermanentlyDeleted: () => void;
}

export const ConversationFooterStatePanel = ({
  state,
  conversation,
  onPermanentlyDeleted,
}: ConversationFooterStatePanelProps) => {
  const { checkValidity } = useGroupRules();
  const { restoreConversation } = useRestoreConversation();
  const { deleteConversation } = useConversationDelete();
  const { updateConversation } = useConversationUpdate();
  const { confirm } = useConfirmationModal();
  const { showSuccessAlert } = useNotifications();

  const onRestoreConversationClick = useCallback(async () => {
    await restoreConversation(conversation.conversationId);
  }, [conversation.conversationId, restoreConversation]);

  const onReopenConversationClick = useCallback(async () => {
    const result = await updateConversation({
      ...conversation,
      conversationStatusId: ConversationStatus.Open,
    });
    // the hook's onError swallows failures into an alert, so the promise resolves either way
    if (result?.data && !result.errors?.length) {
      showSuccessAlert('Conversation reopened');
    }
  }, [conversation, showSuccessAlert, updateConversation]);

  const onPermanentDeleteConversationClick = useCallback(async () => {
    if (
      await confirm({
        title: 'Delete conversation?',
        body: "It can't be restored from Deleted afterwards.",
        confirmLabel: 'Delete permanently',
        danger: true,
      })
    ) {
      const result = await deleteConversation(conversation.conversationId, true);
      if (result?.data && !result.errors?.length) {
        onPermanentlyDeleted();
      }
    }
  }, [confirm, conversation.conversationId, deleteConversation, onPermanentlyDeleted]);

  if (state === ConversationFooterState.Deleted) {
    const canRestore = checkValidity([UserGroupRules.AccessToRestoreDeletedConversations]);
    const canPermanentlyDelete = checkValidity([
      UserGroupRules.AccessToPermanentlyDeleteConversations,
    ]);

    return (
      <ConversationStatePanel
        icon="trash-2"
        tone="danger"
        title="Conversation deleted"
        data-testid="conversation-deleted-panel"
        actions={
          canRestore || canPermanentlyDelete ? (
            <>
              {canPermanentlyDelete ? (
                <StatePanelButton
                  variant="outlined"
                  color="error"
                  onClick={onPermanentDeleteConversationClick}
                  startIcon={
                    <DynamicIcon
                      name="trash-2"
                      library="lucide"
                      size={STATE_PANEL_ICON_SIZE}
                    />
                  }
                >
                  Delete permanently
                </StatePanelButton>
              ) : null}
              {canRestore ? (
                <StatePanelButton
                  variant="contained"
                  onClick={onRestoreConversationClick}
                  startIcon={
                    <DynamicIcon name="undo-2" library="lucide" size={STATE_PANEL_ICON_SIZE} />
                  }
                >
                  Restore
                </StatePanelButton>
              ) : null}
            </>
          ) : undefined
        }
      />
    );
  }

  if (state === ConversationFooterState.Closed) {
    return (
      <ConversationStatePanel
        icon="lock"
        title="Conversation closed"
        description="Closed conversations can't be updated."
        data-testid="conversation-closed-panel"
        actions={
          checkValidity([UserGroupRules.AccessToReopenConversations]) ? (
            <StatePanelButton
              variant="contained"
              onClick={onReopenConversationClick}
              startIcon={
                <DynamicIcon name="lock-open" library="lucide" size={STATE_PANEL_ICON_SIZE} />
              }
              data-testid="conversation-reopen"
            >
              Reopen
            </StatePanelButton>
          ) : undefined
        }
      />
    );
  }

  return (
    <ConversationStatePanel
      icon="eye"
      title="Read only"
      description="Read Only Users do not have access to send messages"
      data-testid="conversation-read-only-panel"
    />
  );
};
